import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketSubmissionWizard = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    clientContactName: '',
    clientContactEmail: '',
    clientContactPhone: '',
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit?.(formData);
    } catch (error) {
      console.error('Error submitting ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Submit Support Ticket</h2>
            <p className="text-sm text-muted-foreground mt-1">Describe your issue and we'll help resolve it</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Contact Information */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData?.clientContactName}
                onChange={(e) => handleChange('clientContactName', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData?.clientContactEmail}
                onChange={(e) => handleChange('clientContactEmail', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder="your.email@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={formData?.clientContactPhone}
                onChange={(e) => handleChange('clientContactPhone', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder="+675 1234 5678"
              />
            </div>

            {/* Issue Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData?.category}
                  onChange={(e) => handleChange('category', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="general">General</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing Question</option>
                  <option value="compliance">Compliance Support</option>
                  <option value="training">Training Request</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="bug_report">Bug Report</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Priority <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData?.priority}
                  onChange={(e) => handleChange('priority', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                >
                  <option value="low">Low - General inquiry</option>
                  <option value="medium">Medium - Standard issue</option>
                  <option value="high">High - Urgent issue</option>
                  <option value="critical">Critical - System down</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData?.subject}
                onChange={(e) => handleChange('subject', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                placeholder="Brief description of your issue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground min-h-[150px]"
                placeholder="Provide detailed information about your issue..."
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t border-border p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Icon name="Send" size={16} className="mr-2" />
                Submit Ticket
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TicketSubmissionWizard;